import { Component, Inject } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  AbstractDialogResultData,
  AbstractFormDialog,
  AbstractFormDialogData,
} from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';
import { AppState } from 'src/app/shared/models';
import Utils from 'src/app/shared/utils';

import { Flock, FlockWeekDataSet, HenDeparture, WeekRegistration } from '../../models/flock.model';
import { upsertFlockRequest } from '../../store/actions/flock.actions';
import { getSelectedFlockWeekData } from '../../store/selectors/flock.selectors';

export interface DepartureDialogInputData extends AbstractFormDialogData {
  flock: Flock;
}

export interface DepartureDialogResultData {
  departure: HenDeparture;
}

@Component({
  selector: 'app-departure-detail',
  templateUrl: './departure-detail.component.html',
  styleUrls: ['./departure-detail.component.scss'],
  standalone: false,
})
export class DepartureDetailComponent extends AbstractFormDialog<DepartureDialogResultData> {
  minDepartureDate: Date | null = null;
  departure: HenDeparture = {
    amount: 0,
    departureDate: null,
    weightInGrams: 0,
    eggsPrice: 12,
    hensPrice: 0.25,
  };

  formGroup = this.fb.group({
    amount: [
      this.departure.amount,
      [Validators.required, Validators.min(0), this.departureAmountValidator()],
    ],
    departureDate: [
      this.departure.departureDate,
      [Validators.required, this.departureDateValidator()],
    ],
    weightInGrams: [
      this.departure.weightInGrams,
      [Validators.required, Validators.min(0), this.departureWeightValidator()],
    ],
    eggsPrice: [this.departure.eggsPrice, [Validators.min(0)]],
    eggsPriceSecondKind: [this.departure.eggsPriceSecondKind, [Validators.min(0)]],
    hensPrice: [this.departure.hensPrice, [Validators.min(0)]],
  });

  minDepartureDate$: Observable<Date> = this.store.select(getSelectedFlockWeekData).pipe(
    take(1),
    map((flockWeekData: FlockWeekDataSet) => this.getMinimumDepartureDate(flockWeekData)),
    tap((minimumDepartureDate) => {
      this.minDepartureDate = minimumDepartureDate;
      this.formGroup.controls.departureDate.updateValueAndValidity();
    }),
  );

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DepartureDialogInputData,
    private readonly fb: FormBuilder,
    protected mdDialogRef: MatDialogRef<
      DepartureDetailComponent,
      AbstractDialogResultData<DepartureDialogResultData>
    >,
    private readonly locale: LocaleService,
    private store: Store<AppState>,
  ) {
    super();
    this.departure = this.data.flock.departure;
    this.minDepartureDate = this.getMinimumDepartureDate();

    this.data.title = `${this.data.flock.name} - ${this.locale.translate(
      'flock-overview.panel.departure-title',
    )}`;

    if (this.departure) {
      this.formGroup.controls.departureDate.patchValue(this.departure.departureDate);
      this.formGroup.controls.amount.patchValue(this.departure.amount);
      this.formGroup.controls.weightInGrams.patchValue(this.departure.weightInGrams);
      this.formGroup.controls.eggsPrice.patchValue(this.departure.eggsPrice ?? 12);
      this.formGroup.controls.eggsPriceSecondKind.patchValue(this.departure.eggsPriceSecondKind);
      this.formGroup.controls.hensPrice.patchValue(this.departure.hensPrice ?? 0.25);
    }
  }

  private getMinimumDepartureDate(flockWeekData?: FlockWeekDataSet): Date | null {
    const arrivalDate = this.data.flock.arrival?.arrivalDate
      ? new Date(this.data.flock.arrival.arrivalDate)
      : null;
    const lastWeekStartDate = flockWeekData?.data?.length
      ? [...flockWeekData.data].sort((left, right) => left.week - right.week)[
          flockWeekData.data.length - 1
        ].weekStartDate
      : null;

    return lastWeekStartDate ? new Date(lastWeekStartDate) : arrivalDate;
  }

  private normalizeDate(date: Date): Date {
    const normalizedDate = new Date(date);
    normalizedDate.setHours(0, 0, 0, 0);
    return normalizedDate;
  }

  public recalculateWeekRegistrations(): WeekRegistration[] {
    // amount of days between chickBirthDate and departureDate
    const daysUntilDeparture = Math.round(
      Math.abs(
        new Date(this.departure.departureDate).getTime() -
          new Date(<string>this.data.flock.arrival.chickBirthDate).getTime(),
      ) /
        (1000 * 3600 * 24),
    );
    const isLastWeekreg = (weeknr: number) =>
      weeknr === Math.max(...this.data.flock.weekRegistrations.map((weekreg) => weekreg.weekNr));

    // if there were weekregistrations entered before (re)setting this departuredate,
    // then the weekregs or the dayregs in the last weekreg beyond the departuredate must be filtered out
    const newWeekRegistrations: WeekRegistration[] = [];
    // first sort in descending order (last week first)
    [...this.data.flock.weekRegistrations]
      .sort((w1, w2) => (w1.weekNr > w2.weekNr ? -1 : 1))
      .forEach((weekReg) => {
        // if there are days beyond the departuredate, filter them out
        if (weekReg.endDay > daysUntilDeparture) {
          // if there are no dayregs then the registration is done weekly,
          // so this should not affect those observations
          if (weekReg.dayRegistrations.length > 0) {
            const filteredDayRegs = weekReg.dayRegistrations.filter(
              (dayReg) => dayReg.day <= daysUntilDeparture,
            );
            // if this weekreg has any days left, then it should remain in the flock data
            if (filteredDayRegs.length > 0) {
              newWeekRegistrations.push({
                ...weekReg,
                dayRegistrations: filteredDayRegs,
                endDay: filteredDayRegs.reduce((reg1, reg2) => (reg1.day > reg2.day ? reg1 : reg2))
                  .day,
              });
            }
          } else {
            // remove the weeks that have a startDay after the departure date
            if (weekReg.startDay <= daysUntilDeparture) {
              // only set the endday of the last week when inserting weekly observations
              newWeekRegistrations.push({
                ...weekReg,
                endDay: daysUntilDeparture,
              });
            }
          }
        } else if (weekReg.endDay < daysUntilDeparture && isLastWeekreg(weekReg.weekNr)) {
          // the dep.date is set later than last day in last week, so missing days should be added to last week
          const newDayRegs = [...weekReg.dayRegistrations];
          let endDay =
            daysUntilDeparture - weekReg.startDay < 7 ? daysUntilDeparture : weekReg.startDay + 6;
          if (weekReg.dayRegistrations.length > 0) {
            const dayNumber = Math.max(...weekReg.dayRegistrations.map((dayreg) => dayreg.day));
            for (let day = dayNumber + 1; day <= daysUntilDeparture; day++) {
              if (newDayRegs.length < 7) {
                newDayRegs.push({
                  day,
                  registrationData: {},
                });
              }
            }
            endDay = Math.max(...newDayRegs.map((dayreg) => dayreg.day));
          }
          newWeekRegistrations.push({
            ...weekReg,
            dayRegistrations: newDayRegs,
            endDay,
          });
        } else {
          // ...else the weekReg can be kept as-is
          newWeekRegistrations.push(weekReg);
        }
      });
    // sort in ascending order like it was in beginning
    return newWeekRegistrations.sort((w1, w2) => (w1.weekNr < w2.weekNr ? -1 : 1));
  }

  save() {
    if (this.formGroup.valid) {
      this.departure = {
        departureDate:
          typeof (<unknown>this.formGroup.controls.departureDate.value) !== 'string'
            ? Utils.toUtcDate(new Date(this.formGroup.controls.departureDate.value)).toISOString()
            : <string>this.formGroup.controls.departureDate.value,
        amount: <number>this.formGroup.controls.amount.value,
        weightInGrams: <number>this.formGroup.controls.weightInGrams.value,
        eggsPrice: this.formGroup.controls.eggsPrice.value,
        eggsPriceSecondKind: this.formGroup.controls.eggsPriceSecondKind.value,
        hensPrice: this.formGroup.controls.hensPrice.value,
      };
      this.store.dispatch(
        upsertFlockRequest({
          request: {
            ...this.data.flock,
            weekRegistrations: this.recalculateWeekRegistrations(),
            departure: this.departure,
          },
        }),
      );
      this.confirm({
        departure: this.departure,
      });
    }
  }

  departureWeightValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (
        control.errors &&
        Object.keys(control.errors).some((err) => err !== 'departureWeightInvalid')
      ) {
        return null;
      }

      const henWeightOnArrival = this.data.flock.arrival?.henWeightInGrams;
      const henWeightOnDeparture = control.value;

      if (!henWeightOnArrival || henWeightOnDeparture > henWeightOnArrival) {
        return null;
      }

      return { departureWeightInvalid: true };
    };
  }

  departureAmountValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const amountOnArrival = this.data.flock.arrival?.amountOfHens;
      const amountOnDeparture = control.value;

      if (amountOnArrival >= amountOnDeparture) {
        return null;
      }

      return { departureAmountInvalid: true };
    };
  }

  departureDateValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (
        control.errors &&
        Object.keys(control.errors).some((err) => err !== 'departureDateBeforeMinimum')
      ) {
        return null;
      }

      if (!control.value || !this.minDepartureDate) {
        return null;
      }

      const selectedDate =
        control.value instanceof Date ? control.value : new Date(control.value as string);

      if (Number.isNaN(selectedDate.getTime())) {
        return null;
      }

      return this.normalizeDate(selectedDate) >= this.normalizeDate(this.minDepartureDate)
        ? null
        : { departureDateBeforeMinimum: true };
    };
  }
}
